import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useResponsivePanelPlacement } from '../../hooks/useResponsivePanelPlacement';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Portal-based popover anchored to a trigger element, holding arbitrary
 * content (a small form, a radio group) rather than a search-and-select list.
 *
 * `SearchPopover` is search-shaped (an input plus a listbox) and
 * `CalendarPopover` is a date grid - neither fits a plain form, so this is a
 * third, minimal shell around the same shared placement algorithm
 * (`useResponsivePanelPlacement`), following the precedent set by
 * `ReportRangeControl.panel.tsx` (a preset list plus a growing custom-date
 * form, no search box): escape-to-close, outside-click-to-close, a focus
 * trap on the mobile sheet, and body-scroll lock while that sheet is open.
 * Extending `useResponsivePanelPlacement` rather than reimplementing it is
 * what keeps this a genuinely new shell and not a duplicated one - see
 * `.claude/rules/verify-load-bearing-claims.md` on extending a shared
 * abstraction instead of bypassing it.
 *
 * A caller that needs a pinned title over a scrolling list passes `header`
 * (and `bodyClassName` for a denser body than a form's `p-4`); passing
 * neither gives the plain padded single-region panel.
 *
 * `data-popover-panel` marks this component's own panel (both branches) and
 * `SearchPopover`'s, so a popover opened FROM INSIDE this one (a client
 * picker nested in a charge-attribution form, say) is recognised as "inside"
 * by the outside-click handler even though it portals to a separate sibling
 * of `document.body` rather than a DOM descendant of this panel.
 */

export interface PopoverProps {
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Accessible name for the panel's `role="dialog"`. */
  ariaLabel: string;
  /** Preferred panel width in pixels on desktop. Defaults to a compact form's width. */
  widthPx?: number;
  /** Tailwind width class applied to the desktop anchored panel. */
  widthClass?: string;
  /**
   * Optional pinned header, rendered above the scrolling body on both
   * branches and never scrolling with it. A panel listing an unbounded number
   * of items needs the title to stay put while they scroll; a short form does
   * not, so this is opt-in and callers that pass nothing get the plain
   * single-region panel they had before.
   */
  header?: ReactNode;
  /**
   * Padding utilities for the scrolling body. The scroll behaviour itself
   * (`min-h-0 flex-1 overflow-y-auto`) is fixed and not overridable - only
   * the padding, so a dense list can sit tighter than a form's `p-4`.
   */
  bodyClassName?: string;
  /**
   * Upper bound on the desktop panel's height as a CSS length (`'66dvh'`), for
   * a long list that should not run the full height of the viewport. The panel
   * still shrinks to the room beside the trigger when that is smaller.
   */
  maxHeightCap?: string;
  children: ReactNode;
}

const DEFAULT_WIDTH_PX = 320;
const DEFAULT_WIDTH_CLASS = 'w-80';
const DEFAULT_BODY_CLASS = 'p-4';

export function Popover({
  triggerRef,
  onClose,
  ariaLabel,
  widthPx = DEFAULT_WIDTH_PX,
  widthClass = DEFAULT_WIDTH_CLASS,
  header,
  bodyClassName = DEFAULT_BODY_CLASS,
  maxHeightCap,
  children,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const { isMobile, placement, top, bottom, left, maxHeight } = useResponsivePanelPlacement({
    triggerRef,
    panelRef,
    widthPx,
  });

  const sheetRef = useFocusTrap(isMobile);

  useBodyScrollLock(isMobile);

  // Escape closes the popover on both branches.
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Outside click is desktop-only: on mobile the dimmed backdrop owns
  // dismissal, so this listener is never registered there. A click landing
  // inside another popover's own portaled panel (e.g. a SearchPopover client
  // picker opened from a control inside this one) is treated as inside too -
  // see the `data-popover-panel` note above.
  useEffect(() => {
    if (isMobile) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-popover-panel]')) return;
      onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile, onClose, triggerRef]);

  // One header-plus-body structure for both branches, so a caller's header
  // pins and its body scrolls identically on desktop and on the mobile sheet.
  const panelContent = (
    <>
      {header && <div className="shrink-0 border-b border-border px-4 py-2.5">{header}</div>}
      <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>
    </>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[9999]">
        <div className="animate-fade-in absolute inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
        <div
          ref={sheetRef}
          data-popover-panel
          role="dialog"
          aria-label={ariaLabel}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          className="animate-fade-up absolute inset-x-0 bottom-0 flex max-h-[80vh] flex-col rounded-t-2xl border-t border-border bg-bg-primary shadow-lg"
        >
          <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-border" aria-hidden="true" />
          {panelContent}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      data-popover-panel
      role="dialog"
      aria-label={ariaLabel}
      data-placement={placement}
      style={{ top, bottom, left, maxHeight: maxHeightCap ? `min(${maxHeight}px, ${maxHeightCap})` : maxHeight }}
      className={`fixed z-[9999] flex flex-col overflow-hidden rounded-lg border border-border bg-bg-primary shadow-lg ${widthClass}`}
    >
      {panelContent}
    </div>,
    document.body,
  );
}
